import type {
  Extension,
  Questionnaire,
  QuestionnaireItem,
} from '@medplum/fhirtypes'
import {
  VALIDATED_TOOL_SUMMARY_ITEM,
  VALIDATED_TOOL_TAG,
} from '#src/models/questionnaires/utils/validatedToolUtils.ts'
import {
  MAX_VALUE,
  MIN_VALUE,
  QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
} from '#src/structureDefinitions.ts'

export const COPM_MAX_ITEMS_PER_SECTION = 5

const extension: Extension[] = [
  {
    url: MIN_VALUE,
    valueInteger: 1,
  },
  {
    url: MAX_VALUE,
    valueInteger: 10,
  },
]

export const copmQuestionnaire = {
  resourceType: 'Questionnaire',
  url: 'https://www.seenhealth.org/fhir/Questionnaire/COPM',
  title: 'The Canadian Occupational Performance Measure (COPM)',
  name: 'COPM',
  status: 'active',
  subjectType: ['Patient'],
  meta: {
    tag: [VALIDATED_TOOL_TAG],
  },
  item: [
    {
      linkId: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
      type: 'display',
      text: 'COPM PDF',
      extension: [
        {
          url: QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL,
          valueString: 'https://blogs.elon.edu/ptkids/files/2012/03/COPM.pdf',
        },
      ],
    },
    {
      linkId: 'self-care',
      type: 'group',
      text: 'Self-care',
      item: [
        {
          linkId: 'self-care-personal-care',
          type: 'group',
          text: 'Personal Care',
          prefix: '(e.g. dressing, bathing, feeding, hygiene)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION).fill(null).flatMap(
            (_, i) =>
              [
                {
                  linkId: `self-care-personal-care-${i}`,
                  type: 'text',
                },
                {
                  linkId: `self-care-personal-care-${i}-importance`,
                  type: 'integer',
                  extension,
                },
              ] satisfies QuestionnaireItem[]
          ),
        },
        {
          linkId: 'self-care-functional-mobility',
          type: 'group',
          text: 'Functional Mobility',
          prefix: '(e.g. walking, climbing stairs, reaching)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `self-care-functional-mobility-${i}`,
                type: 'text',
              },
              {
                linkId: `self-care-functional-mobility-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
        {
          linkId: 'self-care-community-management',
          type: 'group',
          text: 'Community Management',
          prefix: '(e.g. shopping, banking, transportation)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `self-care-community-management-${i}`,
                type: 'text',
              },
              {
                linkId: `self-care-community-management-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
      ],
    },
    {
      linkId: 'productivity',
      type: 'group',
      text: 'Productivity',
      item: [
        {
          linkId: 'productivity-paid-unpaid-work',
          type: 'group',
          text: 'Paid/Unpaid Work',
          prefix: '(e.g., finding/keeping a job, volunteering)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `productivity-paid-unpaid-work-${i}`,
                type: 'text',
              },
              {
                linkId: `productivity-paid-unpaid-work-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
        {
          linkId: 'productivity-household-management',
          type: 'group',
          text: 'Household Management',
          prefix: '(e.g., cleaning, laundry, cooking)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `productivity-household-management-${i}`,
                type: 'text',
              },
              {
                linkId: `productivity-household-management-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
        {
          linkId: 'productivity-play-school',
          type: 'group',
          text: 'Play/School',
          prefix: '(e.g., play skills, homework)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `productivity-play-school-${i}`,
                type: 'text',
              },
              {
                linkId: `productivity-play-school-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
      ],
    },
    {
      linkId: 'leisure',
      type: 'group',
      text: 'Leisure',
      item: [
        {
          linkId: 'leisure-quiet-recreation',
          type: 'group',
          text: 'Quiet Recreation',
          prefix: '(e.g., hobbies, crafts, reading)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `leisure-quiet-recreation-${i}`,
                type: 'text',
              },
              {
                linkId: `leisure-quiet-recreation-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
        {
          linkId: 'leisure-active-recreation',
          type: 'group',
          text: 'Active Recreation',
          prefix: '(e.g., sports, outings, travel)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `leisure-active-recreation-${i}`,
                type: 'text',
              },
              {
                linkId: `leisure-active-recreation-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
        {
          linkId: 'leisure-socialization',
          type: 'group',
          text: 'Socialization',
          prefix: '(e.g., visiting, phone calls, parties, correspondence)',
          item: new Array(COPM_MAX_ITEMS_PER_SECTION)
            .fill(null)
            .flatMap((_, i) => [
              {
                linkId: `leisure-socialization-${i}`,
                type: 'text',
              },
              {
                linkId: `leisure-socialization-${i}-importance`,
                type: 'integer',
                extension,
              },
            ]),
        },
      ],
    },
    {
      linkId: 'occupational-performance-problems',
      type: 'group',
      text: 'Occupational Performance Problems',
      item: new Array(COPM_MAX_ITEMS_PER_SECTION).fill(null).flatMap((_, i) => [
        {
          linkId: `occupational-performance-problems-${i}`,
          type: 'text',
        },
        {
          linkId: `occupational-performance-problems-${i}-performance`,
          type: 'integer',
          extension,
        },
        {
          linkId: `occupational-performance-problems-${i}-satisfaction`,
          type: 'integer',
          extension,
        },
      ]),
    },
    VALIDATED_TOOL_SUMMARY_ITEM,
  ],
} satisfies Questionnaire
